<template>
  <div>
    <!-- Hero Task block -->
    <div class="hero-task-block">
      <div class="hero-task-top">
        <div class="hero-task-label">
          <i class="el-icon-magic-stick" style="font-size:14px"></i>
          Bạn muốn AI làm gì? <span class="req">*</span>
        </div>
        <div class="hero-task-hint">Mô tả yêu cầu phân tích — bạn có thể thêm gợi ý format bên dưới</div>
        <el-input
          ref="taskInput"
          type="textarea"
          :value="value"
          :autosize="{ minRows: 4, maxRows: 8 }"
          :class="{ 'is-error': hasError }"
          placeholder="Ví dụ: Tổng hợp tình hình chăm sóc khách hàng trong tuần — tỷ lệ phản hồi, điểm hài lòng, vấn đề nổi bật và hội thoại cần xử lý ngay."
          @input="$emit('input', $event)" />
      </div>
      <div class="hero-task-actions" v-if="triggerType === 'immediate'">
        <span class="hero-action-hint">Mô tả càng rõ → AI phân tích càng đúng ý</span>
        <el-button size="small" icon="el-icon-view" :disabled="isLoading" @click="$emit('preview')">
          Xem thử (5 KH)
        </el-button>
        <el-button size="small" type="primary" icon="el-icon-video-play" :disabled="isLoading" @click="$emit('run')">
          Chạy phân tích ngay
        </el-button>
      </div>
      <div class="hero-task-actions hero-task-actions--scheduled" v-else>
        <i class="el-icon-alarm-clock"></i>
        <span class="hero-action-hint">
          AI sẽ chạy theo lịch — bấm <strong>"Lưu lại"</strong> ở góc phải để lên lịch.
        </span>
      </div>
    </div>

    <!-- AI context (vai trò + bối cảnh từ /ai-business/zalo) -->
    <SaleAIAiContext :data="businessInfo" @open-setup="$emit('open-setup')" />

    <!-- Output guidance chips -->
    <div class="output-guidance">
      <div class="og-hd">
        <span>Gợi ý format output</span>
        <span style="font-size:9px;color:var(--t4);font-weight:400;letter-spacing:0">Click để thêm vào yêu cầu</span>
      </div>
      <div class="og-chips">
        <div
          v-for="hint in OUTPUT_HINTS"
          :key="hint.text"
          class="og-chip"
          @click="appendHint(hint.text)">
          <span v-html="hint.icon"></span>{{ hint.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SaleAIAiContext from './SaleAIAiContext.vue';

const ICON_LIST = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="9" height="9"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>`;
const ICON_TABLE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="9" height="9"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="12" y1="3" x2="12" y2="21"/></svg>`;
const ICON_GROUP = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="9" height="9"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`;
// const ICON_JSON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="9" height="9"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`;

const OUTPUT_HINTS = [
  { icon: ICON_LIST, label: 'Danh sách insight + hành động', text: 'Liệt kê top hội thoại theo thứ tự ưu tiên giảm dần kèm hành động cụ thể.' },
  { icon: ICON_TABLE, label: 'Dạng bảng', text: 'Trả về dạng bảng tóm tắt với các cột: Hội thoại, Vấn đề, Hành động đề xuất.' },
  { icon: ICON_GROUP, label: 'Phân nhóm', text: 'Phân nhóm hội thoại và giải thích ngắn gọn đặc điểm từng nhóm.' },
  // { icon: ICON_JSON, label: 'Trả về JSON', text: 'Trả về kết quả dưới dạng JSON thuần, không kèm giải thích.' },
];

export default {
  name: 'SaleAITaskSection',
  components: { SaleAIAiContext },
  props: {
    value: { type: String, default: '' },
    businessInfo: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    triggerType: { type: String, default: 'immediate' },
  },
  data() {
    return { OUTPUT_HINTS };
  },
  computed: {
    errorStyle() {
      return this.hasError ? 'border-color: var(--err)' : '';
    },
  },
  methods: {
    appendHint(text) {
      const cur = (this.value || '').trim();
      const next = cur ? cur + '\n' + text : text;
      this.$emit('input', next);
      this.$nextTick(() => { this.focus(); });
    },
    focus() {
      const ref = this.$refs.taskInput;
      if (ref && typeof ref.focus === 'function') ref.focus();
    },
  },
};
</script>
